import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Send, CheckCircle2, ShieldAlert } from "lucide-react";
import { StarsBackground, DISCORD_URL } from "@/components/StarsBackground";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CustomSens() {
  const [config, setConfig] = useState(null);
  const [values, setValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/custom-form/config`)
      .then((r) => {
        setConfig(r.data);
        const init = {};
        r.data.fields.forEach((f) => (init[f.id] = ""));
        setValues(init);
      })
      .catch(() => toast.error("Could not load the form"))
      .finally(() => setLoading(false));
  }, []);

  const setField = (id, v) => setValues((vs) => ({ ...vs, [id]: v }));

  const validate = () => {
    for (const f of config.fields) {
      if (f.required && !(values[f.id] || "").trim()) {
        toast.error(`${f.label} is required`);
        return false;
      }
      if (f.type === "email" && values[f.id]) {
        const ok = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values[f.id]);
        if (!ok) {
          toast.error(`${f.label} must be a valid email`);
          return false;
        }
      }
    }
    return true;
  };

  const onReview = (e) => {
    e.preventDefault();
    if (!validate()) return;
    setAgreed(false);
    setShowDisclaimer(true);
  };

  const onConfirm = async () => {
    if (!agreed) {
      toast.error("Please agree to the disclaimer first");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/custom-orders`, { values });
      setShowDisclaimer(false);
      setSubmitted(true);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Submission failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="relative min-h-screen" data-testid="custom-sens-page">
      <StarsBackground />
      <Header />

      <main className="relative z-10 pt-32 pb-24 md:pt-44">
        <div className="max-w-3xl mx-auto px-6 md:px-10">
          {loading ? (
            <div className="text-white/40 text-sm">Loading form…</div>
          ) : !config ? null : submitted ? (
            <SuccessState />
          ) : (
            <>
              <div className="section-label" data-testid="custom-sens-label">Custom Sens</div>
              <h1
                className="font-display font-black uppercase mt-3 text-3xl sm:text-4xl md:text-5xl lg:text-6xl leading-[1.05]"
                style={{ textWrap: "balance", letterSpacing: "-0.01em" }}
                data-testid="custom-sens-title"
              >
                {config.page_title}
              </h1>
              <p className="text-white/65 mt-5 text-base md:text-lg leading-relaxed" data-testid="custom-sens-subtitle" style={{ textWrap: "balance" }}>
                {config.page_subtitle}
              </p>

              <form onSubmit={onReview} className="form-card mt-12 space-y-7 rounded-xl p-7 md:p-10" data-testid="custom-sens-form">
                {config.fields.map((f) => (
                  <div key={f.id} className="space-y-2" data-testid={`field-row-${f.id}`}>
                    <Label className="text-xs uppercase tracking-widest text-white/70">
                      {f.label}
                      {f.required && <span className="text-[var(--rv-red)] ml-1">*</span>}
                    </Label>
                    {f.type === "textarea" ? (
                      <Textarea
                        value={values[f.id] || ""}
                        onChange={(e) => setField(f.id, e.target.value)}
                        placeholder={f.placeholder || ""}
                        rows={4}
                        className="bg-black/40 border-white/10 text-white"
                        data-testid={`input-${f.id}`}
                      />
                    ) : f.type === "yesno" ? (
                      <div className="grid grid-cols-2 gap-3" data-testid={`input-${f.id}`}>
                        {["Yes", "No"].map((opt) => {
                          const selected = values[f.id] === opt;
                          return (
                            <button
                              key={opt}
                              type="button"
                              onClick={() => setField(f.id, opt)}
                              className={`yesno-btn ${selected ? "yesno-btn-active" : ""}`}
                              data-testid={`input-${f.id}-${opt.toLowerCase()}`}
                            >
                              {opt}
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <Input
                        type={f.type === "number" ? "number" : f.type === "email" ? "email" : "text"}
                        value={values[f.id] || ""}
                        onChange={(e) => setField(f.id, e.target.value)}
                        placeholder={f.placeholder || ""}
                        className="bg-black/40 border-white/10 text-white"
                        data-testid={`input-${f.id}`}
                      />
                    )}
                    {f.help_text && <p className="text-xs text-white/40">{f.help_text}</p>}
                  </div>
                ))}

                <button
                  type="submit"
                  className="btn-primary w-full justify-center"
                  data-testid="submit-form-btn"
                >
                  <Send size={16} /> Review & submit
                </button>
              </form>
            </>
          )}
        </div>
      </main>

      {showDisclaimer && (
        <div
          className="fixed inset-0 z-[70] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !submitting && setShowDisclaimer(false)}
          data-testid="disclaimer-modal"
        >
          <div
            className="max-w-lg w-full bg-[#0a0a0a] border border-white/10 rounded-xl p-8 relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-full bg-[var(--rv-red)]/15 flex items-center justify-center">
                <ShieldAlert size={20} style={{ color: "var(--rv-red)" }} />
              </div>
              <h3 className="font-display font-black uppercase text-xl">Before you submit</h3>
            </div>

            <p className="text-white/70 text-sm leading-relaxed mt-5">{config?.disclaimer}</p>

            <label className="mt-6 flex items-start gap-3 cursor-pointer select-none" data-testid="disclaimer-agree-row">
              <Checkbox
                checked={agreed}
                onCheckedChange={(v) => setAgreed(!!v)}
                className="mt-0.5 border-white/30 data-[state=checked]:bg-[var(--rv-red)] data-[state=checked]:border-[var(--rv-red)]"
                data-testid="disclaimer-checkbox"
              />
              <span className="text-sm text-white/85">I have read and agree to the disclaimer above.</span>
            </label>

            <div className="flex gap-3 mt-7">
              <button
                onClick={() => setShowDisclaimer(false)}
                disabled={submitting}
                className="btn-ghost"
                data-testid="disclaimer-cancel-btn"
              >
                Back
              </button>
              <button
                onClick={onConfirm}
                disabled={!agreed || submitting}
                className="btn-primary flex-1 justify-center"
                data-testid="disclaimer-submit-btn"
              >
                {submitting ? <Loader2 size={16} className="animate-spin" /> : <><Send size={16} /> Send order</>}
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
}

const SuccessState = () => (
  <div className="rv-card rounded-xl p-10 text-center" data-testid="custom-sens-success">
    <CheckCircle2 className="mx-auto" size={48} style={{ color: "#22c55e" }} />
    <h1 className="font-display font-black uppercase mt-5 text-3xl md:text-4xl">Order received</h1>
    <p className="text-white/70 mt-4 text-base">
      Got it. I'll review your specs and DM you on Discord with your custom values.
    </p>
    <a
      href={DISCORD_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="btn-primary mt-7"
      data-testid="success-discord-btn"
    >
      Open Discord
    </a>
  </div>
);
